import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { listenToAuthState, listenToUserDocument } from '../services/auth';
import { DatabaseUser } from '../services/DatabaseUser';
import { DatabaseSettingsModel } from '../services/DatabaseSettings';
import { writeLoggedInCookie } from '../helpers/authCookie';

interface UserContextType {
    user: User | null;
    userData: DatabaseUser | null;
    isAdmin: boolean;
    // Whether the account is allowed past the auth gate — see AuthGate. Admins
    // are always whitelisted; see DatabaseSettingsModel.getAccessStatus.
    isWhitelisted: boolean;
    isLoading: boolean;
}

const UserContext = createContext<UserContextType>({
    user: null,
    userData: null,
    isAdmin: false,
    isWhitelisted: false,
    isLoading: true
});

export const useUser = () => useContext(UserContext);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [userData, setUserData] = useState<any | null>(null);
    const [isAdminUser, setIsAdminUser] = useState(false);
    const [isWhitelistedUser, setIsWhitelistedUser] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [accessLoading, setAccessLoading] = useState(true);

    useEffect(() => {
        // Listen for auth state changes
        const unsubscribeAuth = listenToAuthState((authUser) => {
            setUser(authUser);
            setAuthLoading(false);
            if (!authUser) {
                setAccessLoading(false);
            }

            writeLoggedInCookie(authUser !== null);
        });

        // Cleanup subscription
        return () => unsubscribeAuth();
    }, []);

    // Listen to the user document when auth state changes
    useEffect(() => {
        let unsubscribeUser: () => void;

        if (user) {
            unsubscribeUser = listenToUserDocument(user.uid, (data) => {
                setUserData(data);
            });

            // Check admin and whitelist status (one settings read serves both)
            setAccessLoading(true);
            DatabaseSettingsModel.getAccessStatus(user.email).then(({ isAdmin, isWhitelisted }) => {
                setIsAdminUser(isAdmin);
                setIsWhitelistedUser(isWhitelisted);
                setAccessLoading(false);
            });
        } else {
            setUserData(null);
            setIsAdminUser(false);
            setIsWhitelistedUser(false);
        }

        return () => {
            if (unsubscribeUser) {
                unsubscribeUser();
            }
        };
    }, [user]);

    const isLoading = authLoading || accessLoading;

    const value = {
        user,
        userData,
        isAdmin: isAdminUser,
        isWhitelisted: isWhitelistedUser,
        isLoading
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export default UserContext;
